import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const MicrosoftEmailTest = () => {
    const { instance, accounts } = useMsal();
    const [emails, setEmails] = useState([]);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [signature, setSignature] = useState(localStorage.getItem('emailSignature') || "\nThank you,\nBuilder Playbook Team");
    const [to, setTo] = useState("");
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(false);

    const getAccessToken = async () => {
        const request = {
            scopes: ["Mail.Read", "Mail.Send"],
            account: accounts[0]
        };

        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                return instance.acquireTokenPopup(request).then((response) => {
                    return response.accessToken;
                }).catch((error) => {
                    setStatus("Error acquiring token: " + error.message);
                    return null;
                });
            } else {
                setStatus("Error acquiring token: " + error.message);
                return null;
            }
        }
    };

    const fetchEmails = async () => {
        if (accounts.length === 0) {
            setStatus("Please log in with Microsoft first.");
            return;
        }

        setLoading(true);
        const token = await getAccessToken();
        if (!token) return;

        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/me/mailFolders/Inbox/messages?$top=10&$select=subject,from,receivedDateTime,bodyPreview,isRead", {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setEmails(data.value);
                setStatus("Emails fetched successfully.");
            } else {
                const errorData = await response.json();
                setStatus("Failed to fetch emails: " + (errorData.error?.message || response.statusText));
            }
        } catch (error) {
            setStatus("Error fetching emails: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const importSignature = async () => {
        if (accounts.length === 0) {
            setStatus("Please log in with Microsoft first.");
            return;
        }

        setLoading(true);
        const token = await getAccessToken();
        if (!token) return;

        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/me/mailFolders/SentItems/messages?$top=1&$select=body", {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.value && data.value.length > 0) {
                    let emailBody = data.value[0].body.content;
                    
                    // Attempt to extract content within <body> tags if present
                    const bodyMatch = emailBody.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                    if (bodyMatch) {
                        emailBody = bodyMatch[1];
                    }

                    setStatus("Imported last sent email logic. Please delete the message part and keep only the signature.");
                    setSignature(emailBody);
                } else {
                    setStatus("No sent emails found to import signature from.");
                }
            } else {
                const errorData = await response.json();
                setStatus("Failed to fetch sent items: " + (errorData.error?.message || response.statusText));
            }
        } catch (error) {
            setStatus("Error importing signature: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        if (accounts.length === 0) {
            setStatus("Please log in with Microsoft first.");
            return;
        }

        setLoading(true);
        const token = await getAccessToken();
        if (!token) return;

        // Simple heuristic: If signature contains tags, treat as separate HTML block without newline replacement
        // Otherwise replace newlines
        const finalSignature = signature.includes('<') ? signature : signature.replace(/\n/g, '<br/>');

        const emailData = {
            message: {
                subject: subject,
                body: {
                    contentType: "HTML",
                    content: body.replace(/\n/g, '<br/>') + '<br/><br/>' + finalSignature
                },
                toRecipients: [
                    {
                        emailAddress: {
                            address: to
                        }
                    }
                ]
            },
            saveToSentItems: "true"
        };


        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(emailData)
            });

            if (response.ok) {
                setStatus("Email sent successfully!");
                setSubject("");
                setBody("");
                setTo("");
            } else {
                const errorData = await response.json();
                setStatus("Failed to send email: " + (errorData.error?.message || response.statusText));
            }
        } catch (error) {
            setStatus("Error sending email: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accounts.length > 0) {
            fetchEmails();
        }
    }, [accounts]);

    useEffect(() => {
        localStorage.setItem('emailSignature', signature);
    }, [signature]);

    return (
        <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
            <h2>Microsoft Email Integration Test</h2>
            
            {status && (
                <div style={{ padding: "10px", margin: "10px 0", backgroundColor: "#f0f0f0", borderLeft: "4px solid #0078d4" }}>
                    {status}
                </div>
            )}

            <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
                {/* Inbox Section */}
                <div style={{ flex: 1, border: "1px solid #ddd", padding: "15px", borderRadius: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                        <h3>Inbox (Last 10)</h3>
                        <button onClick={fetchEmails} disabled={loading} style={{ padding: "5px 10px", cursor: "pointer" }}>Refresh</button>
                    </div>

                    {loading && emails.length === 0 ? (
                        <p>Loading emails...</p>
                    ) : (
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            {emails.length === 0 ? (
                                <p>No emails found.</p>
                            ) : (
                                emails.map((email) => (
                                    <div key={email.id} style={{ padding: "10px", borderBottom: "1px solid #eee", backgroundColor: email.isRead ? "white" : "#f0f8ff" }}>
                                        <div style={{ fontWeight: "bold", marginBottom: "5px" }}>{email.from?.emailAddress?.name || "Unknown Sender"}</div>
                                        <div style={{ color: "#0078d4", marginBottom: "5px" }}>{email.subject}</div>
                                        <div style={{ fontSize: "14px", color: "#666" }}>{email.bodyPreview}</div>
                                        <div style={{ fontSize: "12px", color: "#999", marginTop: "5px" }}>
                                            {new Date(email.receivedDateTime).toLocaleString()}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Send Email Section */}
                <div style={{ flex: 1, border: "1px solid #ddd", padding: "15px", borderRadius: "8px" }}>
                    <h3>Send New Email</h3>
                    <form onSubmit={sendEmail}>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>To:</label>
                            <input
                                type="email"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                required
                                style={{ width: "100%", padding: "8px", boxSizing: "border-box" }}
                                placeholder="recipient@example.com"
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>Subject:</label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                required
                                style={{ width: "100%", padding: "8px", boxSizing: "border-box" }}
                                placeholder="Email Subject"
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>Message:</label>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                required
                                style={{ width: "100%", padding: "8px", height: "150px", boxSizing: "border-box" }}
                                placeholder="Type your message here..."
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                                <label style={{ display: "block" }}>Signature (HTML Supported):</label>
                                <button 
                                    type="button" 
                                    onClick={importSignature} 
                                    disabled={loading}
                                    style={{ fontSize: '12px', padding: '2px 5px', cursor: 'pointer' }}
                                >
                                    Import from Sent Items
                                </button>
                            </div>
                            <textarea
                                value={signature}
                                onChange={(e) => setSignature(e.target.value)}
                                style={{ width: "100%", padding: "8px", height: "120px", boxSizing: "border-box", fontFamily: "monospace", fontSize: "12px" }}
                                placeholder="Paste your HTML signature here..."
                            />
                        </div>
                        <button 
                            type="submit" 
                            disabled={loading}
                            style={{ 
                                padding: "10px 20px", 
                                backgroundColor: "#0078d4", 
                                color: "white", 
                                border: "none", 
                                borderRadius: "4px", 
                                cursor: loading ? "not-allowed" : "pointer" 
                            }}
                        >
                            {loading ? "Sending..." : "Send Email"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MicrosoftEmailTest;